// The Budget tab for Residential Prepaid. Its own file so that changing residential's money view cannot
// touch RIC's, which stays inline in ProjectManagementProjectPage under the 'ric' finance model.
//
// The economics: the Fund pays the dealer a share of the System Purchase Price at each reviewed milestone
// the schedule names. The customer's prepayment never enters this project's cash — the dealer collects
// it and bears the risk — so it appears here only as the credit it will become against those payments.
// None of RIC's development fee, change-order waterfall or EPC draw split applies.

import React, { useState } from 'react';
import styled from 'styled-components';
import { toast } from 'react-toastify';
import { strings } from '/imports/strings';
import { formatCalendarDate, roundNumber } from '/imports/utils';
import { css } from '/imports/ui/css';
import { Button } from '/imports/ui/Button';
import { Input } from '/imports/ui/Input';
import { Modal, ModalStyled } from '/imports/ui/Modal';
import { toastOnError } from '/imports/ui/utils';
import {
  ProjectManagementCashEvent, ProjectManagementFundingSource, ProjectManagementMilestone, ProjectManagementParameter,
  projectManagementFundingSourceTypeLabel, projectManagementMilestoneReviewBadge, projectManagementMilestoneReviewLabel,
  projectManagementMoney as money, projectManagementSetDealerSplit, projectManagementUsesLedger,
} from '/imports/api/projectManagement';
import { ProjectManagementDealerPayment, ProjectManagementLifecycleFinance } from '/imports/api/projectManagementLifecycles';
import {
  resTpoDealerShare, resTpoFundUseCodes, resTpoPrepaymentAmountParameters, resTpoPrepaymentCollectedParameter,
} from '/imports/api/projectManagementLifecycleResTpo';

const BudgetStyled = styled.div`
  .fin-section-title { font-weight: 600; margin: 1.25rem 0 0.5rem; display: flex; align-items: center; gap: 0.5rem; }
  .fin-section-title:first-child { margin-top: 0; }
  .fin-section-title .spacer { flex: 1; }
  table { width: 100%; border-collapse: collapse; font-size: 0.9rem; }
  th, td { text-align: left; padding: 0.4rem 0.6rem; border-bottom: 1px solid ${css.grey6}; }
  th { color: ${css.grey2}; font-weight: 600; }
  th.num, td.num { text-align: right; font-variant-numeric: tabular-nums; }
  .muted { color: ${css.grey4}; }
  .sub { font-size: 0.78rem; }
  .badge { font-size: 0.72rem; padding: 0.1rem 0.4rem; border-radius: 0.6rem; border: 1px solid ${css.grey6}; }
  .badge.approved { color: ${css.primaryColor}; }
  .badge.rejected { color: ${css.red1}; }
  tfoot td { font-weight: 600; border-bottom: none; }
  .hint { color: ${css.grey3}; font-size: 0.82rem; margin: 0.25rem 0 0; }
  tr.nested > td { padding: 0.6rem 0 1rem 2.5rem; }
`;

const SplitModalStyled = styled(ModalStyled)`
  .modal-window { width: 34rem; max-width: 100%; }
  .hint { color: ${css.grey3}; font-size: 0.85rem; margin: 0 0 0.9rem; }
  .split-grid { display: grid; grid-template-columns: 1fr 7rem 9rem; gap: 0.5rem 0.75rem; align-items: center; }
  .split-grid .hdr { font-size: 0.75rem; color: ${css.grey4}; text-transform: uppercase; letter-spacing: 0.04em; }
  .split-grid .num { text-align: right; color: ${css.grey3}; font-size: 0.85rem; font-variant-numeric: tabular-nums; }
  .split-grid input { width: 100%; text-align: right; }
  .total { margin-top: 0.75rem; font-size: 0.85rem; color: ${css.grey3}; }
  .total.bad { color: ${css.red2}; }
  .modal-buttons { display: flex; gap: 0.75rem; justify-content: space-between; margin-top: 1.25rem; }
`;

interface Props {
  projectId: string;
  finance: ProjectManagementLifecycleFinance;
  cashEvents: ProjectManagementCashEvent[];
  milestones: ProjectManagementMilestone[];
  parameters: ProjectManagementParameter[];
  fundingSources: ProjectManagementFundingSource[];
  canEdit: boolean;
  // Where the Fund's money comes from and goes is Luminia's own business, so the two fund tables follow
  // the same rule as RIC's Sources panel. A caller who cannot see them was never sent the rows either.
  canSeeFundingSources: boolean;
}

interface PaymentRow {
  row: ProjectManagementDealerPayment;
  milestone?: ProjectManagementMilestone;
  cash?: ProjectManagementCashEvent;
  share: number;
}

const numberOf = (v: unknown) => (typeof v === 'number' ? v : undefined);

/** Per-project override of the dealer payment shares. Mirrors RIC's work-order split modal in shape,
 *  but against the residential schedule and the residential method, so neither can reach the other. */
const DealerSplitModal = (props: { projectId: string; rows: PaymentRow[]; purchasePrice?: number; onClose: () => void }) => {
  const { projectId, rows, purchasePrice, onClose } = props;
  const keys = rows.map((r) => r.row.code);
  const [percents, setPercents] = useState<{ [key: string]: string }>(
    () => Object.fromEntries(rows.map((r) => [r.row.code, String(roundNumber(r.share * 100, 2))])),
  );
  const value = (k: string) => Number(percents[k]);
  const valid = keys.every((k) => (percents[k] || '').trim() !== '' && Number.isFinite(value(k)) && value(k) >= 0 && value(k) <= 100);
  const total = roundNumber(keys.reduce((sum, k) => sum + (Number.isFinite(value(k)) ? value(k) : 0), 0), 2);
  const totals100 = Math.abs(total - 100) < 0.005;
  const save = toastOnError(async () => {
    await projectManagementSetDealerSplit.call({ projectManagementProjectId: projectId, shares: Object.fromEntries(keys.map((k) => [k, roundNumber(value(k) / 100, 4)])) });
    toast.success(strings.DealerSplitSaved());
    onClose();
  });
  return (
    <Modal styled={SplitModalStyled} title={strings.DealerSplit()} onClose={onClose}>
      <p className="hint">{strings.DealerSplitHint()}</p>
      <div className="split-grid">
        <span className="hdr">{strings.Milestone()}</span>
        <span className="hdr num">{strings.ShareOfPurchasePrice()}</span>
        <span className="hdr num">{strings.Estimated()}</span>
        {
          rows.map(({ row, milestone }) => (
            <React.Fragment key={row.code}>
              <span>{milestone?.label || row.milestone}</span>
              <Input type="number" value={percents[row.code]} onChange={(v) => setPercents((prev) => ({ ...prev, [row.code]: v || '' }))} />
              <span className="num">{purchasePrice != null && Number.isFinite(value(row.code)) ? money(roundNumber((purchasePrice * value(row.code)) / 100, 2)) : '—'}</span>
            </React.Fragment>
          ))
        }
      </div>
      <div className={`total${totals100 ? '' : ' bad'}`}>{strings.SharesTotal(total)}{!totals100 && ` · ${strings.DealerSharesMustTotal()}`}</div>
      <div className="modal-buttons">
        <Button className="secondary" onClick={onClose}>{strings.Cancel()}</Button>
        <Button className="primary" disabled={!valid || !totals100} onClick={() => save()}>{strings.Save()}</Button>
      </div>
    </Modal>
  );
};

export const ProjectManagementBudgetResTpo = ({ projectId, finance, cashEvents, milestones, parameters, fundingSources, canEdit, canSeeFundingSources }: Props) => {
  const [showSplit, setShowSplit] = useState(false);
  const param = (name?: string) => (name ? parameters.find((p) => p.name === name) : undefined);
  // Not shown any more, only computed: the dealer's shares are taken against it in the split dialog.
  const priceParam = param(finance.buildCostParameter);
  const purchasePrice = numberOf(priceParam?.actualValue) ?? numberOf(priceParam?.estimatedValue);
  const event = (code: string) => cashEvents.find((c) => c.milestoneKey === code && c.direction === 'out' && c.active !== false);
  const rows: PaymentRow[] = (finance.dealerSchedule || []).map((row) => {
    const cash = event(row.code);
    return { row, milestone: milestones.find((m) => m.key === row.milestone), cash, share: resTpoDealerShare(row, cash) };
  });
  const dealerTotal = rows.reduce((n, r) => n + (r.cash?.estimatedAmount || 0), 0);
  const dealerPaid = rows.reduce((n, r) => n + (r.cash?.actualAmount || 0), 0);
  const overridden = rows.some((r) => typeof r.cash?.drawShare === 'number');
  // Received OR receivable: the credit is contractual, so it is the sum of the amounts whether or not the
  // dealer has collected yet. Collection is shown beside it, not netted into it.
  const prepayments = resTpoPrepaymentAmountParameters.map((name) => ({ name, amount: numberOf(param(name)?.estimatedValue) }));
  const credit = prepayments.some((p) => p.amount !== undefined) ? prepayments.reduce((n, p) => n + (p.amount || 0), 0) : undefined;
  const collected = param(resTpoPrepaymentCollectedParameter)?.estimatedValue;
  const cell = (v?: number) => (v === undefined ? <span className="muted">—</span> : money(v));
  // The Fund's day-one ledger, written by the fund-model import: a project carries it only once that model
  // has priced it, so both tables are absent on a project the model does not name. Read through the same
  // fold as the banner (projectManagementUsesLedger), so the EPC Cost row's Paid is what its dealer
  // payments have paid and this table's total is the tile's figure.
  const fundUses = projectManagementUsesLedger(cashEvents, finance)
    .filter((c) => c.direction === 'out' && c.milestoneKey && resTpoFundUseCodes.includes(c.milestoneKey) && c.active !== false);
  const fundTotal = (rows: { estimatedAmount?: number }[]) => rows.reduce((n, r) => n + (r.estimatedAmount || 0), 0);
  const showFund = canSeeFundingSources && (fundingSources.length > 0 || fundUses.length > 0);
  const buildCostUse = showFund ? fundUses.find((c) => c.milestoneKey === finance.buildCostUseCode) : undefined;

  // The dealer payments: the build-cost Use paid out at the milestones. Under that Use's row when the fund
  // tables list it — the same money, not more of it — and on their own for a project the fund model has
  // not priced, where they are the only Uses there are.
  const dealerPayments = (
    <>
      <div className="fin-section-title">
        {strings.DealerPayments()}
        <span className="spacer" />
        {canEdit && rows.length > 0 && rows.every((r) => r.cash) &&
          <Button className="secondary compact" onClick={() => setShowSplit(true)}>{strings.DealerSplit()}</Button>}
      </div>
      <table>
        <thead>
          <tr>
            <th>{strings.Milestone()}</th><th>{strings.Status()}</th><th className="num">{strings.ShareOfPurchasePrice()}</th><th className="num">{strings.Gross()}</th><th className="num">{strings.Paid()}</th>
          </tr>
        </thead>
        <tbody>
          {
            rows.map(({ row, milestone, cash, share }) => (
              <tr key={row.code}>
                <td>{milestone?.label || row.milestone}</td>
                <td>
                  {
                    milestone?.reviewStatus
                      ? <span className={`badge ${projectManagementMilestoneReviewBadge(milestone.reviewStatus)}`}>{projectManagementMilestoneReviewLabel(milestone.reviewStatus)}</span>
                      : <span className="muted">—</span>
                  }
                </td>
                <td className="num">
                  {Math.round(share * 1000) / 10}%
                  {typeof cash?.drawShare === 'number' && <span className="muted sub"> *</span>}
                </td>
                <td className="num">{cell(cash?.estimatedAmount)}</td>
                <td className="num">{cell(cash?.actualAmount)}</td>
              </tr>
            ))
          }
        </tbody>
        <tfoot>
          <tr><td colSpan={3}>{strings.Total()}</td><td className="num">{money(dealerTotal)}</td><td className="num">{money(dealerPaid)}</td></tr>
        </tfoot>
      </table>
      {overridden && <div className="hint">* {strings.DealerSplitHint()}</div>}
    </>
  );

  return (
    <BudgetStyled>

      {
        showFund &&
        <>
          <div className="fin-section-title">{strings.FundSources()}</div>
          <table>
            <thead>
              <tr><th>{strings.Source()}</th><th>{strings.Party()}</th><th className="num">{strings.Estimated()}</th></tr>
            </thead>
            <tbody>
              {
                fundingSources.map((s) => (
                  <tr key={s._id}>
                    <td>{s.note || projectManagementFundingSourceTypeLabel(s.type)}</td>
                    <td>{s.party || <span className="muted">—</span>}</td>
                    <td className="num">{cell(s.estimatedAmount)}</td>
                  </tr>
                ))
              }
            </tbody>
            <tfoot>
              <tr><td colSpan={2}>{strings.Total()}</td><td className="num">{money(fundTotal(fundingSources))}</td></tr>
            </tfoot>
          </table>

          <div className="fin-section-title">{strings.FundUses()}</div>
          <table>
            <thead>
              <tr><th>{strings.Use()}</th><th className="num">{strings.Estimated()}</th><th className="num">{strings.Paid()}</th></tr>
            </thead>
            <tbody>
              {
                fundUses.map((c) => (
                  <React.Fragment key={c._id}>
                    <tr>
                      <td>{c.label || c.milestoneKey}</td>
                      <td className="num">{cell(c.estimatedAmount)}</td>
                      <td className="num">{cell(c.actualAmount)}</td>
                    </tr>
                    {c === buildCostUse && <tr className="nested"><td colSpan={3}>{dealerPayments}</td></tr>}
                  </React.Fragment>
                ))
              }
            </tbody>
            <tfoot>
              <tr>
                <td>{strings.Total()}</td>
                <td className="num">{money(fundTotal(fundUses))}</td>
                <td className="num">{money(fundUses.reduce((n, c) => n + (c.actualAmount || 0), 0))}</td>
              </tr>
            </tfoot>
          </table>
          <div className="hint">{strings.FundSourcesUsesHint()}</div>
        </>
      }

      {!buildCostUse && dealerPayments}

      <div className="fin-section-title">{strings.CustomerPrepayment()}</div>
      <table>
        <thead>
          <tr><th>{strings.Name()}</th><th className="num">{strings.Amount()}</th><th>{strings.Collected()}</th></tr>
        </thead>
        <tbody>
          {prepayments.map((p) => (
            <tr key={p.name}><td>{p.name}</td><td className="num">{cell(p.amount)}</td><td className="muted">—</td></tr>
          ))}
        </tbody>
        <tfoot>
          <tr>
            <td>{strings.Total()}</td>
            <td className="num">{cell(credit)}</td>
            <td>{formatCalendarDate(collected instanceof Date ? collected : undefined) || <span className="muted">—</span>}</td>
          </tr>
        </tfoot>
      </table>
      <div className="hint">{strings.CustomerPrepaymentHint()}</div>

      {showSplit && <DealerSplitModal projectId={projectId} rows={rows} purchasePrice={purchasePrice} onClose={() => setShowSplit(false)} />}
    </BudgetStyled>
  );
};
